/**
 * FS-709: every chart that plots pressure in Pa also carries inches of water
 * column on its right-hand axis. This is the one conversion, the tick wording,
 * and the reference-label placement that keeps that right-hand column clear.
 */

/** Pa to inches of water column (1 in. w.c. = 249.08 Pa). */
export const PA_TO_INH2O = 0.004014;

/** Recharts anchors a rotated axis title at one end, so it ran from the
 *  middle of the axis toward the top and "Pressure Delta (inH₂O)" ran 3 px
 *  off the top of its chart. The CSS property outranks recharts' attribute. */
export const AXIS_TITLE_STYLE = { textAnchor: 'middle' as const };

/** Horizontal x-axis tick labels sit level with the bottom inH₂O tick, and the
 *  last date label ran 3 px into it; 8 px (recharts' default is 2) clears it
 *  at every domain, because the bottom tick is always the domain floor. */
export const X_TICK_MARGIN = 8;

/** Decimals that keep neighbouring inH₂O ticks distinct over this Pa span.
 *  Two places hold for the pressure charts' 30-300 Pa spans; the delta charts'
 *  10-20 Pa spans step under 0.01 in., where two places would print the same
 *  label on neighbouring rows. span / 5 sits under recharts' smallest step for
 *  a fixed domain (span / 4, rounded up, last gap at least 0.99 of a step). */
export const inH2ODecimals = (spanPa: number): number => {
  const step = (spanPa * PA_TO_INH2O) / 5;
  return step > 0 ? Math.max(2, Math.ceil(-Math.log10(step))) : 2;
};

/** Recharts tickFormatter for the right-hand axis, given the shared Pa domain. */
export const inH2OTickFormatter =
  ([lo, hi]: [number, number]) =>
  (pa: number): string =>
    (pa * PA_TO_INH2O).toFixed(inH2ODecimals(hi - lo));

/**
 * The one explicit Pa pair a chart hands both of its axes. Recharts resolves
 * each axis's domain from the series bound to it alone, and the inH₂O axis has
 * none: given 'auto' it drew no ticks at all, and given a function it collapsed
 * to one tick mid-plot. Whole-Pa snug bounds over the plotted values, reaching
 * zero from whichever side the data sits on when asked, never zero-height.
 */
export const sharedPaDomain = (min: number, max: number, includeZero: boolean): [number, number] => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  const lo = Math.floor(includeZero ? Math.min(0, min) : min);
  const hi = Math.ceil(includeZero ? Math.max(0, max) : max);
  return [lo, Math.max(hi, lo + 1)];
};

/**
 * A reference line's label, drawn inside the plot at its left edge. The
 * right-hand margin belongs to the inH₂O ticks; a label placed there landed on
 * a tick row whenever its line came within a few pixels of one, which depends
 * on the domain and so changed with the include-zero checkbox.
 *
 * The text goes on the requested side of the line unless the line sits in the
 * top or bottom tenth of the domain, where it flips inward so the text stays
 * inside the plot (a tenth is ~28 px on the shortest of these charts; the text
 * needs 17). Callers put the lower of two nearby lines' labels below and the
 * upper one's above, so the pair never stack onto each other.
 */
export const referenceLabel = (
  value: string,
  y: number,
  [lo, hi]: [number, number],
  side: 'above' | 'below',
) => {
  const at = hi > lo ? (y - lo) / (hi - lo) : 0.5;
  const above = at >= 0.9 ? false : at <= 0.1 ? true : side === 'above';
  // On a horizontal line, recharts' insideBottom* is the strip just above the
  // line and insideTop* the strip just below it.
  return {
    value,
    position: above ? ('insideBottomLeft' as const) : ('insideTopLeft' as const),
    fontSize: 12,
  };
};

/** A round inch step giving about six ticks over the span, and the places
 *  that print it: 2.5 × 10^n needs one more than its power of ten, 10 × 10^n
 *  one fewer. */
const niceInchStep = (spanIn: number): { step: number; places: number } => {
  const raw = spanIn / 6;
  const exp = Math.floor(Math.log10(raw));
  const off = (m: number) => Math.abs(Math.log((m * 10 ** exp) / raw));
  const unit = [1, 2, 2.5, 5, 10].reduce((best, m) => (off(m) < off(best) ? m : best));
  const places = -exp + (unit === 2.5 ? 1 : unit === 10 ? -1 : 0);
  return { step: unit * 10 ** exp, places: Math.max(2, places) };
};

/**
 * Plotly has no scaled twin axis, so the right-hand inH₂O axis `matches` the
 * Pa axis (same range through every zoom, pan and axis drag) and names its own
 * ticks: tickvals in Pa, ticktext in inches, at round inch values over the
 * visible range. Ticks outside the range are dropped by Plotly.
 */
export const inH2OPlotlyTicks = (lo: number, hi: number): { tickvals: number[]; ticktext: string[] } => {
  const loIn = lo * PA_TO_INH2O;
  const hiIn = hi * PA_TO_INH2O;
  if (!(hiIn > loIn)) return { tickvals: [], ticktext: [] };
  const { step, places } = niceInchStep(hiIn - loIn);
  const first = Math.ceil(loIn / step - 1e-9);
  const last = Math.floor(hiIn / step + 1e-9);
  const tickvals: number[] = [];
  const ticktext: string[] = [];
  for (let i = first; i <= last; i++) {
    const inches = i * step;
    tickvals.push(inches / PA_TO_INH2O);
    // i * step can land on -0 or 0.30000000000000004; toFixed tidies both
    // except the sign of zero.
    ticktext.push((i === 0 ? 0 : inches).toFixed(places));
  }
  return { tickvals, ticktext };
};
